/**
 * Impression ticket thermique 80mm — agent Windows si actif,
 * sinon téléchargement PDF pour imprimer autrement.
 */

export function extractTicketJobId(data) {
  return data?.job_id || data?.job?.id || null;
}

export function notifyTicketQueued(data) {
  const id = extractTicketJobId(data);
  const short = id ? `${String(id).slice(0, 8)}…` : '';
  alert(`Ticket envoyé à l'imprimante${short ? ` (${short})` : ''}.`);
}

function notifyPdfFallback(reason) {
  const prefix = reason === 'error'
    ? 'Impression impossible'
    : 'Imprimante inactive';
  alert(`${prefix} — ticket PDF téléchargé. Vous pouvez l'imprimer autrement.`);
}

function triggerPdfDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 5000);
}

function openPdfInNewTab(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) {
    triggerPdfDownload(blob, filename);
    alert('Autorisez les popups pour voir le ticket, ou ouvrez le PDF telecharge.');
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

async function blobFromResponse(response, fallbackMessage) {
  const data = response.data;
  if (data && data.type && String(data.type).includes('application/json')) {
    const text = await data.text();
    let detail = fallbackMessage;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.detail) detail = parsed.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return new Blob([data], { type: 'application/pdf' });
}

function safeFilenamePart(value) {
  return String(value || 'ticket').replace(/[^\w.-]+/g, '_').slice(0, 80);
}

async function isTicketPrinterActive(api, magasinId) {
  try {
    const [health, status] = await Promise.all([
      api.get('/print/agent/health').catch(() => null),
      api.get('/printer/agent-status', {
        params: magasinId ? { magasin_id: magasinId } : {},
      }).catch(() => null),
    ]);
    return Boolean(health?.data?.any_online || status?.data?.online);
  } catch {
    return false;
  }
}

async function fetchTicketPdfBlob(api, opts) {
  const {
    type,
    ref,
    payload,
    variant,
    vendeur,
    dateDebut,
    dateFin,
  } = opts || {};

  if (type === 'calcul' && payload) {
    const response = await api.post('/calcul/pdf?action=download', payload, { responseType: 'blob' });
    const blob = await blobFromResponse(response, 'Telechargement PDF impossible');
    return { blob, filename: 'ticket-calcul.pdf' };
  }

  const docType = type === 'repair' ? 'sav' : type;
  let docRef = ref;
  if (type === 'rapport_vendeur') {
    docRef = `${vendeur}|${dateDebut}|${dateFin}`;
  }
  if (!docRef) throw new Error('Reference ticket manquante');

  const response = await api.get('/print/ticket.pdf', {
    params: {
      doc_type: docType,
      doc_ref: docRef,
      variant: variant || undefined,
    },
    responseType: 'blob',
  });
  const blob = await blobFromResponse(response, 'Telechargement PDF impossible');
  return {
    blob,
    filename: `ticket-${safeFilenamePart(docType)}-${safeFilenamePart(docRef)}.pdf`,
  };
}

/**
 * Telecharge le PDF 80mm du ticket (tous types).
 * @param {import('axios').AxiosInstance} api
 * @param {object} opts memes champs que printTicket80mm
 */
export async function downloadTicketPdf(api, opts) {
  const { blob, filename } = await fetchTicketPdfBlob(api, opts);
  triggerPdfDownload(blob, filename);
  return { fallback: 'pdf' };
}

/** Ouvre le PDF du ticket dans un onglet (sans impression thermique). */
export async function openTicketPdf(api, opts) {
  const { blob, filename } = await fetchTicketPdfBlob(api, opts);
  openPdfInNewTab(blob, filename);
  return { ok: true, view: 'pdf' };
}

/** Télécharge le ticket d’avoir 80mm en PDF. */
export async function downloadAvoirTicketPdf(api, ref) {
  return downloadTicketPdf(api, { type: 'avoir', ref });
}

/**
 * @param {import('axios').AxiosInstance} api
 * @param {{
 *   type: 'vente'|'repair'|'acompte'|'commande'|'sav'|'calcul'|'calcul_doc'|'rapport_vendeur'|'caisse'|'avoir',
 *   ref?: string,
 *   magasinId?: string,
 *   payload?: object,
 *   variant?: string,
 *   vendeur?: string,
 *   dateDebut?: string,
 *   dateFin?: string,
 *   quiet?: boolean,
 * }} opts
 */
export async function printTicket80mm(api, opts) {
  const {
    type,
    ref,
    magasinId,
    payload,
    variant,
    vendeur,
    dateDebut,
    dateFin,
    quiet = false,
  } = opts;

  const printerActive = await isTicketPrinterActive(api, magasinId);
  if (!printerActive) {
    await downloadTicketPdf(api, opts);
    notifyPdfFallback('offline');
    return { ok: true, fallback: 'pdf', printer_online: false };
  }

  try {
    let res;
    switch (type) {
      case 'vente':
        res = await api.post(`/ventes/${encodeURIComponent(ref)}/print-ticket-caisse`);
        break;
      case 'repair':
        // Registre complet 80mm (escpos_sav) — compatible agent Windows v1.1.
        res = await api.post('/printer/jobs/typed', {
          type: 'ticket',
          repair_ref: ref,
          ...(magasinId ? { magasin_id: magasinId } : {}),
        });
        break;
      case 'acompte':
      case 'commande':
      case 'sav':
      case 'calcul_doc':
      case 'avoir':
        res = await api.post('/print/silent', {
          doc_type: type,
          doc_ref: ref,
          ...(magasinId ? { magasin_id: magasinId } : {}),
        });
        break;
      case 'calcul':
        res = await api.post('/calcul/print-ticket', payload);
        break;
      case 'rapport_vendeur':
        res = await api.post('/print/silent', {
          doc_type: 'rapport_vendeur',
          doc_ref: `${vendeur}|${dateDebut}|${dateFin}`,
          ...(magasinId ? { magasin_id: magasinId } : {}),
        });
        break;
      case 'caisse':
        res = await api.post('/print/silent', {
          doc_type: 'caisse',
          doc_ref: ref,
          variant: variant || 'fermeture',
          ...(magasinId ? { magasin_id: magasinId } : {}),
        });
        break;
      default:
        throw new Error(`Type ticket 80mm inconnu: ${type}`);
    }

    if (!quiet) notifyTicketQueued(res.data);
    return { ...res.data, printer_online: true };
  } catch (err) {
    try {
      await downloadTicketPdf(api, opts);
    } catch {
      throw err;
    }
    notifyPdfFallback('error');
    return { ok: true, fallback: 'pdf', printer_online: true, printer_error: err.message };
  }
}

/** Mappe un document « tous documents » vers printTicket80mm. */
export async function printDocumentTicket80mm(api, doc, { quiet } = {}) {
  if (!doc) return null;
  if (doc.source === 'vente_historique') {
    return printTicket80mm(api, { type: 'vente', ref: doc.ref, quiet });
  }
  if (doc.source === 'calcul') {
    return printTicket80mm(api, { type: 'calcul_doc', ref: doc.id, quiet });
  }
  if (doc.source === 'commande') {
    return printTicket80mm(api, { type: 'commande', ref: doc.id, quiet });
  }
  if (doc.source === 'reparation') {
    return printTicket80mm(api, { type: 'repair', ref: doc.ref, quiet });
  }
  return null;
}
